import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';
import { chatgenieAPI } from 'src/config/api';
import useToast from 'src/hooks/useToast';
import { updatePartnerClientsData } from 'src/redux/modules/deployment-center/actions';
import ActionModal from 'src/components/ActionModal';
import ConfirmModal from 'src/components/ConfirmModal';
import SimpleButton from 'src/components/SimpleButton';
import { List, ListItem } from './ActionsSelector.style';
import Popover from '../Popover';

const MESSENGER_STATES = {
  ENABLED: 'ENABLED',
  DISABLED: 'DISABLED'
};

const actionSelectorsData = [
  {
    itemText: 'Enable Messenger',
    actionType: MESSENGER_STATES.ENABLED,
    modalConfirmTxt: 'enable Messenger'
  },
  {
    itemText: 'Disable Messenger',
    actionType: MESSENGER_STATES.DISABLED,
    modalConfirmTxt: 'disable Messenger'
  }
];

const ActionsSelector = ({
  checkedCompanies,
  onClickCb,
  clientsData,
  updatePartnerClientsData
}) => {
  const { toast } = useToast();
  const [confirmModalState, setConfirmModalState] = useState({
    isOpen: false,
    actionType: '',
    modalConfirmTxt: ''
  });

  const { mutateAsync: updateMessengerStatuses } = useMutation({
    mutationFn: async payload => {
      const result = await chatgenieAPI.updateMessengerStatuses(payload);
      return { payload, result };
    },
    onSuccess: ({ payload, result } = {}) => {
      if (result?.status === 200 && payload?.companyIds && clientsData.length) {
        const newClientsData = clientsData.map(client => {
          if (payload.companyIds.includes(client.company_id)) {
            return {
              ...client,
              is_messenger_enabled: payload.action === MESSENGER_STATES.ENABLED
            };
          }
          return client;
        });
        if (newClientsData) {
          updatePartnerClientsData(newClientsData);
        }
        toast.success(`Messenger has been ${payload.action.toLowerCase()} for these clients`);
      }
    },
    onError: error => {
      toast.error(error.message);
    },
    onSettled: () => {
      onClickCb();
    }
  });

  const handleActionSelectorClick = data => {
    if (!checkedCompanies?.length > 0) {
      toast.warn('Nothing was selected');
      return;
    }

    setConfirmModalState({
      isOpen: true,
      actionType: data.actionType,
      modalConfirmTxt: data.modalConfirmTxt
    });
  };

  const handleConfirmModal = async () => {
    await updateMessengerStatuses({
      companyIds: checkedCompanies,
      action: confirmModalState.actionType
    });
    setConfirmModalState({ isOpen: false, actionType: '', modalConfirmTxt: '' });
  };

  const handleCancelModal = () => {
    setConfirmModalState({ isOpen: false, actionType: '', modalConfirmTxt: '' });
  };

  const renderActionListItems = () =>
    actionSelectorsData.map(data => (
      <ListItem type="button" onClick={() => handleActionSelectorClick(data)}>
        {data.itemText}
      </ListItem>
    ));

  return (
    <div>
      <Popover title="Actions" closeOnSelect>
        <List>{renderActionListItems()}</List>
      </Popover>
      <ActionModal
        isOpen={false}
        title="Confirm edit"
        description={`Are you sure you want to ${confirmModalState.action} Messenger for these clients?`}
      >
        <SimpleButton type="button">Confirm</SimpleButton>
        <SimpleButton type="button" styleMode="outline">
          Cancel
        </SimpleButton>
      </ActionModal>

      <ConfirmModal
        title="Confirm edit"
        description={`Are you sure you want to ${confirmModalState.modalConfirmTxt} Messenger for these clients?`}
        isOpen={confirmModalState.isOpen}
        cancelText="Cancel"
        submitText="Confirm"
        toggleHandler={handleCancelModal}
        onSubmit={handleConfirmModal}
      />
    </div>
  );
};

ActionsSelector.defaultProps = {
  clientsData: []
};

ActionsSelector.propTypes = {
  onClickCb: PropTypes.func.isRequired,
  checkedCompanies: PropTypes.arrayOf(PropTypes.any).isRequired,
  updatePartnerClientsData: PropTypes.func.isRequired,
  clientsData: PropTypes.arrayOf(PropTypes.any)
};

const mapStateToProps = state => ({
  clientsData: state.deploymentCenterReducer.clients.data || []
});

const mapDispatchToProps = dispatch => ({
  updatePartnerClientsData: payload => dispatch(updatePartnerClientsData(payload))
});

export default connect(mapStateToProps, mapDispatchToProps)(ActionsSelector);
